import type { WorkspaceRole } from '@/api/queries'

const ROLE_RANK: Record<WorkspaceRole, number> = {
  viewer: 0,
  editor: 1,
  admin: 2,
  owner: 3,
}

/**
 * Mirrors ``app.services.permissions.role_satisfies`` in the backend.
 * Returns true if ``role`` is at least ``min`` (i.e. it grants every
 * capability ``min`` does, plus possibly more).
 *
 * Use this for disabling controls in the UI — the backend re-checks on
 * every mutating request, so the UI gate is a usability layer, not a
 * security boundary. When the backend ultimately denies a request the
 * route surfaces the error via ``formatError`` + a toast.
 */
export function roleSatisfies(role: WorkspaceRole, min: WorkspaceRole): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[min]
}

export function canCreateProjects(role: WorkspaceRole): boolean {
  return roleSatisfies(role, 'editor')
}

export function canEditProjects(role: WorkspaceRole): boolean {
  return roleSatisfies(role, 'editor')
}

export function canDeleteProjects(role: WorkspaceRole): boolean {
  // Backend gates project delete behind admin (folders gate at editor).
  return roleSatisfies(role, 'admin')
}

export function canManageFolders(role: WorkspaceRole): boolean {
  return roleSatisfies(role, 'editor')
}

export function canMoveDocuments(role: WorkspaceRole): boolean {
  return roleSatisfies(role, 'editor')
}
